// ── List scroll restore ─────────────────────────────────────────────────────
// Shared wiring for "come Back from a detail page and the list is exactly where
// you left it". Used by the dashboard (Views), the directory (/more/:category)
// and Deals (/deals) — three separate list implementations that all want the
// same behaviour.
//
// A component opts in with three things:
//   1. mix this in
//   2. render a `data-service-id` on each card/row
//   3. call rememberListScroll() in the card's click handler, BEFORE any await,
//      and applyListScroll(items) from a watcher on its results array
//
// The pending entry is deliberately non-reactive (`this._pendingListScroll`) —
// it must never trigger a re-render.
//
// See utils/listScrollMemory.js for the storage/anchoring details, and note the
// warning there about window.scrollY being useless in this app.

import {
  takeListScroll,
  restoreListScroll,
} from "@/utils/listScrollMemory";

export default {
  created() {
    // Runs before the component's own created(), so a fetch kicked off there
    // already sees the pending entry.
    this._pendingListScroll = takeListScroll(
      (this.$route && this.$route.path) || "/"
    );
    this._cancelListScroll = null;
  },

  beforeUnmount() {
    // Stop any in-flight restore so its timers can't scroll whatever page
    // replaces this one.
    this.discardListScroll();
  },

  methods: {
    /**
     * Apply the remembered position once the list has actually rendered. Safe
     * to call on every change of the results array — it only fires once, on the
     * first non-empty render after a Back.
     */
    applyListScroll(items) {
      if (!this._pendingListScroll) return;
      if (!items || !items.length) return;

      const entry = this._pendingListScroll;
      this._pendingListScroll = null;
      this.$nextTick(() => {
        this._cancelListScroll = restoreListScroll(entry);
      });
    },

    /**
     * Abandon a pending or in-flight restore. Call from every action that
     * changes what the list shows — filter, sort, page, category, reset —
     * because replaying an old position onto different results is wrong.
     */
    discardListScroll() {
      this._pendingListScroll = null;
      if (this._cancelListScroll) {
        this._cancelListScroll();
        this._cancelListScroll = null;
      }
    },
  },
};
